import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { SERVICES } from '../data/services';
import { LOCATIONS } from '../data/locations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPES = ['A single feature (patio, fire pit, etc.)', 'A combination of features', 'A complete backyard transformation', 'Not sure yet — I need guidance'];
const TIMELINES = ['As soon as possible', 'Within 3 months', '3–6 months', 'Planning ahead / next season'];
const CONTACT_METHODS = ['Phone', 'Email', 'Text'];

const StepShell = ({ children, stepKey }) => (
  <motion.div
    key={stepKey}
    initial={{ opacity: 0, x: 32 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -32 }}
    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const QuoteForm = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    projectTypes: [], address: '', city: '', scope: '', details: '', timeline: '',
    name: '', email: '', phone: '', preferredContact: 'Phone', website: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) =>
    set('projectTypes', form.projectTypes.includes(t) ? form.projectTypes.filter((x) => x !== t) : [...form.projectTypes, t]);

  const canNext = () => {
    if (step === 0) return form.projectTypes.length > 0;
    if (step === 1) return form.city.trim().length > 0;
    if (step === 2) return form.scope.length > 0;
    return form.name.trim() && form.email.trim() && form.phone.trim();
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name, email: form.email, phone: form.phone,
        address: form.address, city: form.city,
        project_types: form.projectTypes, scope: form.scope,
        details: form.details, timeline: form.timeline,
        preferred_contact: form.preferredContact, website: form.website,
        page: window.location.pathname,
      });
      navigate('/thank-you/');
    } catch (e) {
      setError('Something went wrong sending your request. Please try again or call us at (402) 202-9111.');
      setSubmitting(false);
    }
  };

  const steps = ['Project', 'Location', 'Details', 'Contact'];

  return (
    <div data-testid="quote-form" className={embedded ? '' : 'border border-line bg-paper p-6 shadow-[0_30px_80px_-30px_rgba(22,22,21,0.25)] sm:p-10'}>
      <div className="mb-8 flex items-center gap-2" aria-label={`Step ${step + 1} of 4`}>
        {steps.map((label, i) => (
          <div key={label} className="flex-1">
            <div className={`h-1 transition-colors duration-500 ${i <= step ? 'bg-bronze' : 'bg-line'}`} />
            <span className={`mt-2 block text-[10px] font-semibold uppercase tracking-[0.2em] ${i <= step ? 'text-ink' : 'text-muted2/60'}`}>{label}</span>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <StepShell stepKey="s0">
            <h2 className="font-serif text-3xl font-medium text-ink">What are you dreaming of?</h2>
            <p className="mt-2 text-sm text-muted2">Select everything that applies.</p>
            <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {SERVICES.map((s) => {
                const active = form.projectTypes.includes(s.navTitle);
                return (
                  <button
                    key={s.slug}
                    type="button"
                    data-testid={`quote-type-${s.slug}`}
                    onClick={() => toggleType(s.navTitle)}
                    aria-pressed={active}
                    className={`flex items-center justify-between border px-4 py-3.5 text-left text-sm font-medium transition-colors ${
                      active ? 'border-bronze bg-bronze/10 text-ink' : 'border-line bg-white/50 text-ink/70 hover:border-ink/40'
                    }`}
                  >
                    {s.navTitle}
                    {active && <Check className="h-4 w-4 text-bronze" />}
                  </button>
                );
              })}
            </div>
          </StepShell>
        )}

        {step === 1 && (
          <StepShell stepKey="s1">
            <h2 className="font-serif text-3xl font-medium text-ink">Where's the project?</h2>
            <p className="mt-2 text-sm text-muted2">We serve the Omaha &amp; Lincoln metro areas.</p>
            <div className="mt-6 space-y-4">
              <div>
                <label htmlFor="quote-city" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">City *</label>
                <select id="quote-city" data-testid="quote-city-select" value={form.city} onChange={(e) => set('city', e.target.value)} className="input-field">
                  <option value="">Select your city…</option>
                  {LOCATIONS.map((l) => <option key={l.slug} value={l.city}>{l.city}, NE</option>)}
                  <option value="Other">Other / nearby community</option>
                </select>
              </div>
              <div>
                <label htmlFor="quote-address" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Street address (optional)</label>
                <input id="quote-address" data-testid="quote-address-input" value={form.address} onChange={(e) => set('address', e.target.value)} className="input-field" placeholder="So we can prepare for your consultation" />
              </div>
            </div>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell stepKey="s2">
            <h2 className="font-serif text-3xl font-medium text-ink">Tell us about the project</h2>
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Project scope *</label>
                <div className="grid gap-2.5">
                  {SCOPES.map((s) => (
                    <button
                      key={s} type="button" data-testid={`quote-scope-${s.slice(0, 18).replace(/[^a-z]/gi, '-').toLowerCase()}`}
                      onClick={() => set('scope', s)} aria-pressed={form.scope === s}
                      className={`border px-4 py-3 text-left text-sm transition-colors ${form.scope === s ? 'border-bronze bg-bronze/10 text-ink' : 'border-line bg-white/50 text-ink/70 hover:border-ink/40'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="quote-timeline" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Timeline</label>
                <select id="quote-timeline" data-testid="quote-timeline-select" value={form.timeline} onChange={(e) => set('timeline', e.target.value)} className="input-field">
                  <option value="">Select a timeline…</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="quote-details" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Anything else we should know?</label>
                <textarea id="quote-details" data-testid="quote-details-textarea" value={form.details} onChange={(e) => set('details', e.target.value)} rows={3} className="input-field resize-none" placeholder="Size of your space, ideas you love, must-haves…" />
              </div>
            </div>
          </StepShell>
        )}

        {step === 3 && (
          <StepShell stepKey="s3">
            <h2 className="font-serif text-3xl font-medium text-ink">How do we reach you?</h2>
            <p className="mt-2 text-sm text-muted2">We'll follow up to schedule your free on-site consultation.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="quote-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Full name *</label>
                <input id="quote-name" data-testid="quote-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} className="input-field" autoComplete="name" />
              </div>
              <div>
                <label htmlFor="quote-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Email *</label>
                <input id="quote-email" type="email" data-testid="quote-email-input" value={form.email} onChange={(e) => set('email', e.target.value)} className="input-field" autoComplete="email" />
              </div>
              <div>
                <label htmlFor="quote-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Phone *</label>
                <input id="quote-phone" type="tel" data-testid="quote-phone-input" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="input-field" autoComplete="tel" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Preferred contact method</label>
                <div className="flex gap-2.5">
                  {CONTACT_METHODS.map((m) => (
                    <button key={m} type="button" data-testid={`quote-contact-${m.toLowerCase()}`} onClick={() => set('preferredContact', m)} aria-pressed={form.preferredContact === m}
                      className={`flex-1 border px-4 py-3 text-sm transition-colors ${form.preferredContact === m ? 'border-bronze bg-bronze/10 text-ink' : 'border-line bg-white/50 text-ink/70'}`}>{m}</button>
                  ))}
                </div>
              </div>
              <input type="text" name="website" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            </div>
            {error && <p data-testid="quote-form-error" className="mt-4 text-sm font-medium text-red-700">{error}</p>}
          </StepShell>
        )}
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        {step > 0 ? (
          <button type="button" data-testid="quote-back-button" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-sm font-semibold text-muted2 transition-colors hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : <span />}
        {step < 3 ? (
          <button type="button" data-testid="quote-next-button" onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-40">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" data-testid="quote-submit-button" onClick={submit} disabled={!canNext() || submitting}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-40">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : 'Request My Free Quote'}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteForm;
